import React, {useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Detail, FlexColumn, FlexRow, Text} from '@forecast-it/design-system';
import {NovaGroundingBreakdown, NovaGroundingEntry, NovaGroundingReason} from '../shared/novaGroundingBreakdown';
import {NovaCapUnit} from '../shared/novaVariableTruncation';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NOVA_MONOSPACE_FONT} from '../shared/novaStudioFonts';

const REASON_MESSAGE_ID: Record<NovaGroundingReason, string> = {
	[NovaGroundingReason.NoValue]: 'nova.grounding.reasonNoValue',
	[NovaGroundingReason.NotPermitted]: 'nova.grounding.reasonNotPermitted',
	[NovaGroundingReason.RemovedVariable]: 'nova.grounding.reasonRemoved',
	[NovaGroundingReason.UnknownName]: 'nova.grounding.reasonUnknown',
	[NovaGroundingReason.DetachedFile]: 'nova.grounding.reasonDetachedFile',
	[NovaGroundingReason.AttachedFile]: 'nova.grounding.reasonAttachedFile',
};

const Panel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

// Four lists side by side where there is room, stacking on a narrow column rather than scrolling.
const Lists = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 16px;
`;

// The token is shown in the same fixed-width font as the editor it came from, so the author can match
// it against what they typed character for character — the same reasoning as NovaInputIssues.
const Token = styled.span`
	font-family: ${NOVA_MONOSPACE_FONT};
`;

// `data-testid` is carried on the plain elements this component owns rather than on a design-system
// component: `Text`, `FlexRow` and `FlexColumn` destructure the props they support and forward
// nothing else, and this repo sets no RTL `testIdAttribute`, so a testid handed to one of them would
// simply never reach the DOM.
const Headline = styled.div``;
const ListBody = styled.div``;

const HeadlineRow = styled.div`
	display: flex;
	align-items: baseline;
	gap: 12px;
`;

// A real <button> for the aria-expanded and keyboard semantics the design-system layout primitives
// do not provide — the same reasoning as the variable library's collapsible bar.
const DetailsToggle = styled.button`
	all: unset;
	margin-left: auto;
	cursor: pointer;
	text-decoration: underline;

	&:focus-visible {
		outline: 2px solid ${NovaStudioColor.focusRing};
		outline-offset: 2px;
	}
`;

interface ListProps {
	labelId: string;
	entries: NovaGroundingEntry[];
	dataCy: string;
}

interface Props {
	breakdown: NovaGroundingBreakdown;
	dataCy: string;
}

// One run's grounding, in the four categories the author needs to tell apart: what reached the model
// whole, what was left out, what was cut, and what pointed at nothing. Every list renders even when
// empty — "None" is a result the author can trust, whereas a section that quietly disappears leaves
// them wondering whether it was checked at all.
export function NovaGroundingBreakdownPanel({breakdown, dataCy}: Props): JSX.Element {
	const {formatMessage, formatNumber} = useIntl();
	// Closed by default: the headline is the answer most of the time, and four lists under every answer
	// buried it. The lists stay one click away rather than being dropped, because "which reference was
	// left out, and why" is the question this panel exists to answer once the headline looks wrong.
	const [isOpen, setIsOpen] = useState(false);

	const describeTruncation = (entry: NovaGroundingEntry): string => {
		const messageId =
			entry.unit === NovaCapUnit.Entries ? 'nova.grounding.truncatedEntries' : 'nova.grounding.truncatedCharacters';
		return formatMessage(
			{id: messageId},
			{
				original: formatNumber(entry.originalSize ?? 0),
				capped: formatNumber(entry.cappedSize ?? 0),
			}
		);
	};

	// A truncated entry describes itself by size; every other kind that needs describing does so by
	// reason. A resolved entry needs neither — the list it sits in already says what happened.
	const describe = (entry: NovaGroundingEntry): string | null => {
		if (entry.originalSize !== undefined) {
			return describeTruncation(entry);
		}
		if (entry.reason !== undefined) {
			return formatMessage({id: REASON_MESSAGE_ID[entry.reason]});
		}
		return null;
	};

	const List = ({labelId, entries, dataCy: listDataCy}: ListProps): JSX.Element => (
		<FlexColumn gap="s">
			<Text>{formatMessage({id: labelId})}</Text>
			<ListBody data-testid={listDataCy} data-cy={listDataCy}>
				{entries.length === 0 ? (
					<Detail>{formatMessage({id: 'nova.grounding.emptyList'})}</Detail>
				) : (
					entries.map(entry => {
						const description = describe(entry);
						return (
							<FlexRow key={`${entry.input}-${entry.token}`} gap="s">
								<Token>{entry.token}</Token>
								{description === null ? null : <Detail>{description}</Detail>}
							</FlexRow>
						);
					})
				)}
			</ListBody>
		</FlexColumn>
	);

	return (
		<Panel data-testid={dataCy} data-cy={dataCy}>
			<HeadlineRow>
				<Headline data-testid={`${dataCy}-headline`} data-cy={`${dataCy}-headline`}>
					<Text>
						{formatMessage(
							{id: 'nova.grounding.headline'},
							{
								sources: breakdown.sourceCount,
								tokens: breakdown.approximateTokens,
								withheld: breakdown.withheldCount,
							}
						)}
					</Text>
				</Headline>
				<DetailsToggle
					type="button"
					data-testid={`${dataCy}-details-toggle`}
					data-cy={`${dataCy}-details-toggle`}
					aria-expanded={isOpen}
					onClick={() => setIsOpen(previous => !previous)}
				>
					<Detail>{formatMessage({id: 'nova.grounding.detailsToggle'})}</Detail>
				</DetailsToggle>
			</HeadlineRow>
			{isOpen ? (
				<Lists>
					<List labelId="nova.grounding.resolvedLabel" entries={breakdown.resolved} dataCy={`${dataCy}-resolved`} />
					<List labelId="nova.grounding.skippedLabel" entries={breakdown.skipped} dataCy={`${dataCy}-skipped`} />
					<List
						labelId="nova.grounding.truncatedLabel"
						entries={breakdown.truncated}
						dataCy={`${dataCy}-truncated`}
					/>
					<List labelId="nova.grounding.brokenLabel" entries={breakdown.broken} dataCy={`${dataCy}-broken`} />
				</Lists>
			) : null}
		</Panel>
	);
}
